import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  Grid,
  Input,
  InputLabel,
  FormControl,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Campaign as CampaignIcon,
  CloudUpload as CloudUploadIcon,
  Info as InfoIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { bannerAPI } from '../../services/api';

interface MainBanner {
  bannerId: number;
  pcImageUrl: string;
  mobileImageUrl: string;
  linkUrl: string;
  startDate: string;
  endDate: string;
  isActive: boolean;
  createdAt: string;
  locale: string;
}

interface CreateBannerRequest {
  pcImageUrl: string;
  mobileImageUrl: string;
  linkUrl: string;
  startDate: number;
  endDate: number;
  isActive: boolean;
  weight: number;
  locale: string;
}

interface MainBannerTabProps {
  onError: (message: string) => void;
  onSuccess: (message: string) => void;
}

const MainBannerTab: React.FC<MainBannerTabProps> = ({ onError, onSuccess }) => {
  const [banners, setBanners] = useState<MainBanner[]>([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [guidelineDialogOpen, setGuidelineDialogOpen] = useState(false);
  const [editingBanner, setEditingBanner] = useState<MainBanner | null>(null);
  const [createForm, setCreateForm] = useState<CreateBannerRequest>({
    pcImageUrl: '',
    mobileImageUrl: '',
    linkUrl: '',
    startDate: Math.floor(Date.now() / 1000), // 현재 UTC 시간
    endDate: Math.floor((Date.now() + (30 * 24 * 60 * 60 * 1000)) / 1000), // 30일 후 UTC 시간
    isActive: true,
    weight: 1,
    locale: 'all',
  });
  const [editForm, setEditForm] = useState<CreateBannerRequest>({
    pcImageUrl: '',
    mobileImageUrl: '',
    linkUrl: '',
    startDate: Math.floor(Date.now() / 1000), // 현재 UTC 시간
    endDate: Math.floor((Date.now() + (30 * 24 * 60 * 60 * 1000)) / 1000), // 30일 후 UTC 시간
    isActive: true,
    weight: 1,
    locale: 'all',
  });

  // 파일 업로드 refs
  const pcImageRef = useRef<HTMLInputElement>(null);
  const mobileImageRef = useRef<HTMLInputElement>(null);
  const editPcImageRef = useRef<HTMLInputElement>(null);
  const editMobileImageRef = useRef<HTMLInputElement>(null);

  // 파일 상태
  const [pcImageFile, setPcImageFile] = useState<File | null>(null);
  const [mobileImageFile, setMobileImageFile] = useState<File | null>(null);
  const [editPcImageFile, setEditPcImageFile] = useState<File | null>(null);
  const [editMobileImageFile, setEditMobileImageFile] = useState<File | null>(null);

  // 배너 목록 로드
  const loadBanners = async () => {
    try {
      setLoading(true);
      const response = await bannerAPI.getMainBanners();

      
      // API 응답 구조: { success: true, data: [...] }
      const rawBanners = response.data?.data || [];
      
      // 배열이 아닌 경우 빈 배열로 설정
      const bannersArray = Array.isArray(rawBanners) ? rawBanners : [];
      

      
      setBanners(bannersArray);
    } catch (err: any) {
      console.error('Main Banners Error:', err);
      onError(err.response?.data?.error || '메인 배너 목록을 불러오는데 실패했습니다.');
      setBanners([]); // 에러 시 빈 배열로 설정
    } finally {
      setLoading(false);
    }
  };

  // 배너 생성
  const handleCreateBanner = async () => {
    try {
      // FormData 생성
      const formData = new FormData();
      formData.append('link_url', createForm.linkUrl);
      formData.append('start_date', createForm.startDate.toString());
      formData.append('end_date', createForm.endDate.toString());
      formData.append('is_active', createForm.isActive.toString());
      formData.append('weight', createForm.weight.toString());
      formData.append('locale', createForm.locale);


      // 파일 추가
      if (pcImageFile) {
        formData.append('pc_image_file', pcImageFile);
      }
      if (mobileImageFile) {
        formData.append('mobile_image_file', mobileImageFile);
      }

      await bannerAPI.createMainBanner(formData);
      onSuccess('메인 배너가 성공적으로 생성되었습니다.');
      setCreateDialogOpen(false);
      setCreateForm({
        pcImageUrl: '',
        mobileImageUrl: '',
        linkUrl: '',
        startDate: Math.floor(Date.now() / 1000), // 현재 UTC 시간
        endDate: Math.floor((Date.now() + (30 * 24 * 60 * 60 * 1000)) / 1000), // 30일 후 UTC 시간
        isActive: true,
        weight: 1,
        locale: 'all',
      });
      // 파일 상태 초기화
      setPcImageFile(null);
      setMobileImageFile(null);
      loadBanners();
    } catch (err: any) {
      onError(err.response?.data?.error || '메인 배너 생성에 실패했습니다.');
    }
  };

  // 배너 삭제
  const handleDeleteBanner = async (bannerId: number) => {
    if (!window.confirm('정말로 이 배너를 삭제하시겠습니까?')) {
      return;
    }

    try {
      await bannerAPI.deleteMainBanner(bannerId);
      onSuccess('배너가 성공적으로 삭제되었습니다.');
      loadBanners();
    } catch (err: any) {
      onError(err.response?.data?.error || '배너 삭제에 실패했습니다.');
    }
  };

  // 배너 수정
  const handleEditBanner = async (bannerId: number) => {
    const banner = banners.find(b => b.bannerId === bannerId);
    if (!banner) {
      onError('배너를 찾을 수 없습니다.');
      return;
    }

    // banner.startDate와 banner.endDate는 문자열 날짜 형태 ("2025-08-25 12:00:00")
    const startDateTimestamp = Math.floor(new Date(banner.startDate + 'Z').getTime() / 1000);
    const endDateTimestamp = Math.floor(new Date(banner.endDate + 'Z').getTime() / 1000);
    

    
    setEditForm({
      pcImageUrl: banner.pcImageUrl,
      mobileImageUrl: banner.mobileImageUrl,
      linkUrl: banner.linkUrl,
      startDate: startDateTimestamp,
      endDate: endDateTimestamp,
      isActive: banner.isActive,
      weight: 1, // 기본값 설정
      locale: banner.locale || 'all',
    });
    setEditingBanner(banner);
    setEditDialogOpen(true);
  };

  // 배너 수정 저장
  const handleUpdateBanner = async () => {
    if (!editingBanner) return;

    try {

      
      // FormData 생성
      const formData = new FormData();
      formData.append('link_url', editForm.linkUrl);
      formData.append('start_date', editForm.startDate.toString());
      formData.append('end_date', editForm.endDate.toString());
      formData.append('is_active', editForm.isActive.toString());
      formData.append('weight', editForm.weight.toString());
      formData.append('locale', editForm.locale);

      // 파일 추가
      if (editPcImageFile) {
        formData.append('pc_image_file', editPcImageFile);
      }
      if (editMobileImageFile) {
        formData.append('mobile_image_file', editMobileImageFile);
      }

      await bannerAPI.updateMainBanner(editingBanner.bannerId, formData);
      onSuccess('배너가 성공적으로 수정되었습니다.');
      setEditDialogOpen(false);
      setEditingBanner(null);
      setEditForm({
        pcImageUrl: '',
        mobileImageUrl: '',
        linkUrl: '',
        startDate: Math.floor(Date.now() / 1000),
        endDate: Math.floor(Date.now() / 1000) + (30 * 24 * 60 * 60),
        isActive: true,
        weight: 1,
        locale: 'all',
      });
      // 파일 상태 초기화
      setEditPcImageFile(null);
      setEditMobileImageFile(null);
      loadBanners();
    } catch (err: any) {
      onError(err.response?.data?.error || '배너 수정에 실패했습니다.');
    }
  };

  // 파일 업로드 처리
  const handleFileUpload = (file: File, type: 'pc' | 'mobile', isEdit: boolean = false) => {
    if (!file) return;

    // 파일 크기 체크 (5MB)
    if (file.size > 5 * 1024 * 1024) {
      onError('파일 크기는 5MB 이하여야 합니다.');
      return;
    }

    // 파일 타입 체크
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      onError('JPG, JPEG, PNG, GIF 파일만 업로드 가능합니다.');
      return;
    }

    if (isEdit) {
      if (type === 'pc') {
        setEditPcImageFile(file);
      } else {
        setEditMobileImageFile(file);
      }
    } else {
      if (type === 'pc') {
        setPcImageFile(file);
      } else {
        setMobileImageFile(file);
      }
    }
  };

  // 파일 업로드 버튼 클릭
  const handleUploadClick = (type: 'pc' | 'mobile', isEdit: boolean = false) => {
    if (isEdit) {
      if (type === 'pc') {
        editPcImageRef.current?.click();
      } else {
        editMobileImageRef.current?.click();
      }
    } else {
      if (type === 'pc') {
        pcImageRef.current?.click();
      } else {
        mobileImageRef.current?.click();
      }
    }
  };

  useEffect(() => {
    loadBanners();
  }, []); // 빈 의존성 배열로 마운트 시에만 실행

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <Typography>로딩 중...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* 헤더 */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
          Main Banner List
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateDialogOpen(true)}
        >
          Add Banner
        </Button>
      </Box>

      {/* 배너 목록 */}
      <Card elevation={2}>
        <CardContent>
          <TableContainer component={Paper} elevation={0}>
            <Table>
                              <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Image</TableCell>
                    <TableCell>Link URL</TableCell>
                    <TableCell>Start Date (KST)</TableCell>
                    <TableCell>End Date (KST)</TableCell>
                    <TableCell>Locale</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Created At</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
              <TableBody>
                {banners.map((banner) => (
                  <TableRow key={banner.bannerId} hover>
                    <TableCell>{banner.bannerId}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                          <Chip label="PC" size="small" color="primary" />
                          <Avatar
                            src={banner.pcImageUrl}
                            variant="rounded"
                            sx={{ width: 60, height: 40 }}
                          >
                            <CampaignIcon />
                          </Avatar>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                          <Chip label="Mobile" size="small" color="secondary" />
                          <Avatar
                            src={banner.mobileImageUrl}
                            variant="rounded"
                            sx={{ width: 60, height: 40 }}
                          >
                            <CampaignIcon />
                          </Avatar>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ maxWidth: 200, wordBreak: 'break-all' }}>
                        {banner.linkUrl ? (
                          <a 
                            href={banner.linkUrl} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            style={{ 
                              color: '#1976d2', 
                              textDecoration: 'underline',
                              cursor: 'pointer'
                            }}
                          >
                            {banner.linkUrl}
                          </a>
                        ) : (
                          '-'
                        )}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {banner.startDate && banner.startDate !== '0' 
                          ? (() => {
                              // 문자열 날짜를 Date 객체로 변환 (UTC 시간)
                              const utcDate = new Date(banner.startDate + 'Z'); // UTC로 명시
                              if (isNaN(utcDate.getTime())) return 'Not set';
                              return utcDate.toLocaleString('ko-KR', {
                                year: 'numeric',
                                month: '2-digit',
                                day: '2-digit',
                                hour: '2-digit',
                                minute: '2-digit',
                                timeZone: 'Asia/Seoul',
                              });
                            })()
                          : 'Not set'
                        }
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {banner.endDate && banner.endDate !== '0'
                          ? (() => {
                              // 문자열 날짜를 Date 객체로 변환 (UTC 시간)
                              const utcDate = new Date(banner.endDate + 'Z'); // UTC로 명시
                              if (isNaN(utcDate.getTime())) return 'Not set';
                              return utcDate.toLocaleString('ko-KR', {
                                year: 'numeric',
                                month: '2-digit',
                                day: '2-digit',
                                hour: '2-digit',
                                minute: '2-digit',
                                timeZone: 'Asia/Seoul',
                              });
                            })()
                          : 'Not set'
                        }
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={banner.locale || 'all'}
                        size="small"
                        color={banner.locale === 'all' ? 'default' : 'info'}
                        variant={banner.locale === 'all' ? 'outlined' : 'filled'}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={banner.isActive ? '활성' : '비활성'}
                        color={banner.isActive ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {banner.createdAt
                          ? new Date(banner.createdAt).toLocaleDateString('ko-KR', {
                              year: 'numeric',
                              month: '2-digit',
                              day: '2-digit',
                              timeZone: 'Asia/Seoul',
                            })
                          : '-'
                        }
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                        <IconButton
                          size="small"
                          onClick={() => handleEditBanner(banner.bannerId)}
                          title="Edit"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteBanner(banner.bannerId)}
                          title="Delete"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* 배너 생성 다이얼로그 */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Add New Main Banner</Typography>
            <Button
              startIcon={<InfoIcon />}
              onClick={() => setGuidelineDialogOpen(true)}
              size="small"
              sx={{ color: '#1976d2' }}
            >
              Banner Guideline
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                    PC Image
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Button
                      variant="outlined"
                      startIcon={<CloudUploadIcon />}
                      onClick={() => handleUploadClick('pc')}
                      sx={{ minWidth: 120 }}
                    >
                      Upload PC Image
                    </Button>
                    {pcImageFile && (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography variant="body2" color="success.main">
                          ✓ {pcImageFile.name}
                        </Typography>
                        <Box
                          component="img"
                          src={URL.createObjectURL(pcImageFile)}
                          alt="PC Image Preview"
                          sx={{
                            width: '100%',
                            maxWidth: 500,
                            height: 'auto',
                            maxHeight: 300,
                            objectFit: 'contain',
                            border: '1px solid #e0e0e0',
                            borderRadius: 1,
                            cursor: 'pointer',
                            '&:hover': {
                              opacity: 0.8,
                              borderColor: '#1976d2'
                            }
                          }}
                          onClick={() => window.open(URL.createObjectURL(pcImageFile), '_blank')}
                          title="Click to view full image"
                        />
                      </Box>
                    )}
                  </Box>
                  <input
                    id="pc-image-upload"
                    type="file"
                    ref={pcImageRef}
                    onChange={(e) => {
                      const file = (e.target as HTMLInputElement).files?.[0];
                      if (file) handleFileUpload(file, 'pc');
                    }}
                    style={{ display: 'none' }}
                    accept="image/jpeg,image/jpg,image/png,image/gif"
                  />
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                    Mobile Image
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Button
                      variant="outlined"
                      startIcon={<CloudUploadIcon />}
                      onClick={() => handleUploadClick('mobile')}
                      sx={{ minWidth: 120 }}
                    >
                      Upload Mobile Image
                    </Button>
                    {mobileImageFile && (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography variant="body2" color="success.main">
                          ✓ {mobileImageFile.name}
                        </Typography>
                        <Box
                          component="img"
                          src={URL.createObjectURL(mobileImageFile)}
                          alt="Mobile Image Preview"
                          sx={{
                            width: '100%',
                            maxWidth: 500,
                            height: 'auto',
                            maxHeight: 300,
                            objectFit: 'contain',
                            border: '1px solid #e0e0e0',
                            borderRadius: 1,
                            cursor: 'pointer',
                            '&:hover': {
                              opacity: 0.8,
                              borderColor: '#1976d2'
                            }
                          }}
                          onClick={() => window.open(URL.createObjectURL(mobileImageFile), '_blank')}
                          title="Click to view full image"
                        />
                      </Box>
                    )}
                  </Box>
                  <input
                    id="mobile-image-upload"
                    type="file"
                    ref={mobileImageRef}
                    onChange={(e) => {
                      const file = (e.target as HTMLInputElement).files?.[0];
                      if (file) handleFileUpload(file, 'mobile');
                    }}
                    style={{ display: 'none' }}
                    accept="image/jpeg,image/jpg,image/png,image/gif"
                  />
                </Box>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Link URL"
                  value={createForm.linkUrl}
                  onChange={(e) => setCreateForm({ ...createForm, linkUrl: e.target.value })}
                  margin="normal"
                  required
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Start Date (KST)"
                  type="datetime-local"
                  value={(() => {
                    const date = new Date(createForm.startDate * 1000);
                    const kstDate = new Date(date.getTime() + (9 * 60 * 60 * 1000)); // UTC+9
                    // datetime-local 형식: YYYY-MM-DDTHH:MM (초 제거)
                    const year = kstDate.getUTCFullYear();
                    const month = String(kstDate.getUTCMonth() + 1).padStart(2, '0');
                    const day = String(kstDate.getUTCDate()).padStart(2, '0');
                    const hours = String(kstDate.getUTCHours()).padStart(2, '0');
                    const minutes = String(kstDate.getUTCMinutes()).padStart(2, '0');
                    const result = `${year}-${month}-${day}T${hours}:${minutes}`;
                    

                    
                    return result;
                  })()}
                  onChange={(e) => {
                    // 사용자가 입력한 KST 시간을 UTC timestamp로 변환
                    const inputValue = e.target.value; // "2025-08-25T21:00"
                    
                    // 입력값을 직접 파싱하여 KST 시간 생성
                    const [datePart, timePart] = inputValue.split('T');
                    const [year, month, day] = datePart.split('-').map(Number);
                    const [hour, minute] = timePart.split(':').map(Number);
                    
                    // KST 시간을 UTC timestamp로 직접 변환
                    // KST = UTC + 9시간이므로, UTC = KST - 9시간
                    let utcHour = hour - 9;
                    let utcDay = day;
                    let utcMonth = month;
                    let utcYear = year;
                    
                    // 시간이 음수가 되면 이전 날로 조정
                    if (utcHour < 0) {
                      utcHour += 24;
                      utcDay -= 1;
                      // 날짜가 0이 되면 이전 달로 조정
                      if (utcDay <= 0) {
                        utcMonth -= 1;
                        if (utcMonth <= 0) {
                          utcMonth = 12;
                          utcYear -= 1;
                        }
                        // 해당 월의 마지막 날 계산
                        const lastDay = new Date(utcYear, utcMonth, 0).getDate();
                        utcDay = lastDay;
                      }
                    }
                    
                    const utcTimestamp = Date.UTC(utcYear, utcMonth - 1, utcDay, utcHour, minute, 0);
                    const timestamp = Math.floor(utcTimestamp / 1000);
                    

                    setCreateForm({ ...createForm, startDate: timestamp });
                  }}
                  margin="normal"
                  required
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="End Date (KST)"
                  type="datetime-local"
                  value={(() => {
                    const date = new Date(createForm.endDate * 1000);
                    const kstDate = new Date(date.getTime() + (9 * 60 * 60 * 1000)); // UTC+9
                    // datetime-local 형식: YYYY-MM-DDTHH:MM (초 제거)
                    const year = kstDate.getUTCFullYear();
                    const month = String(kstDate.getUTCMonth() + 1).padStart(2, '0');
                    const day = String(kstDate.getUTCDate()).padStart(2, '0');
                    const hours = String(kstDate.getUTCHours()).padStart(2, '0');
                    const minutes = String(kstDate.getUTCMinutes()).padStart(2, '0');
                    const result = `${year}-${month}-${day}T${hours}:${minutes}`;
                    

                    
                    return result;
                  })()}
                  onChange={(e) => {
                    // 사용자가 입력한 KST 시간을 UTC timestamp로 변환
                    const inputValue = e.target.value; // "2025-09-24T21:00"
                    
                    // 입력값을 직접 파싱하여 KST 시간 생성
                    const [datePart, timePart] = inputValue.split('T');
                    const [year, month, day] = datePart.split('-').map(Number);
                    const [hour, minute] = timePart.split(':').map(Number);
                    
                    // KST 시간을 UTC timestamp로 직접 변환
                    // KST = UTC + 9시간이므로, UTC = KST - 9시간
                    let utcHour = hour - 9;
                    let utcDay = day;
                    let utcMonth = month;
                    let utcYear = year;
                    
                    // 시간이 음수가 되면 이전 날로 조정
                    if (utcHour < 0) {
                      utcHour += 24;
                      utcDay -= 1;
                      // 날짜가 0이 되면 이전 달로 조정
                      if (utcDay <= 0) {
                        utcMonth -= 1;
                        if (utcMonth <= 0) {
                          utcMonth = 12;
                          utcYear -= 1;
                        }
                        // 해당 월의 마지막 날 계산
                        const lastDay = new Date(utcYear, utcMonth, 0).getDate();
                        utcDay = lastDay;
                      }
                    }
                    
                    const utcTimestamp = Date.UTC(utcYear, utcMonth - 1, utcDay, utcHour, minute, 0);
                    const timestamp = Math.floor(utcTimestamp / 1000);
                    

                    setCreateForm({ ...createForm, endDate: timestamp });
                  }}
                  margin="normal"
                  required
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Locale</InputLabel>
                  <Select
                    value={createForm.locale}
                    label="Locale"
                    onChange={(e) => setCreateForm({ ...createForm, locale: e.target.value })}
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="en">English (en)</MenuItem>
                    <MenuItem value="ko">Korean (ko)</MenuItem>
                    <MenuItem value="ja">Japanese (ja)</MenuItem>
                    <MenuItem value="zh">Chinese (zh)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Weight"
                  type="number"
                  value={createForm.weight}
                  onChange={(e) => setCreateForm({ ...createForm, weight: parseInt(e.target.value) })}
                  margin="normal"
                  required
                />
              </Grid>
              <Grid item xs={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={createForm.isActive}
                      onChange={(e) => setCreateForm({ ...createForm, isActive: e.target.checked })}
                    />
                  }
                  label="Active Status"
                  sx={{ mt: 2 }}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateBanner} variant="contained">
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* 가이드라인 다이얼로그 */}
      <Dialog open={guidelineDialogOpen} onClose={() => setGuidelineDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Banner Guideline</Typography>
            <IconButton onClick={() => setGuidelineDialogOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography variant="h6" gutterBottom sx={{ color: '#2e7d32', fontWeight: 'bold' }}>
              Home Page Banner Ad
            </Typography>
            
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                Image Dimensions:
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Box sx={{ 
                  width: 120, 
                  height: 40, 
                  bgcolor: '#e0e0e0', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  border: '1px solid #ccc',
                  borderRadius: 1
                }}>
                  <Typography variant="caption">480×160</Typography>
                </Box>
                <Box sx={{ 
                  width: 120, 
                  height: 34, 
                  bgcolor: '#e0e0e0', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  border: '1px solid #ccc',
                  borderRadius: 1
                }}>
                  <Typography variant="caption">840×136</Typography>
                </Box>
              </Box>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                Specifications:
              </Typography>
              <Box sx={{ bgcolor: '#f5f5f5', p: 2, borderRadius: 1 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  • <strong>Image Format:</strong> PNG, JPG, JPEG
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  • <strong>File Size:</strong> Maximum 100KB for each banner
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  • <strong>Content:</strong> Company / Product / Service / Project name
                </Typography>
                <Typography variant="body2">
                  • <strong>Quality:</strong> High resolution, clear and readable text
                </Typography>
              </Box>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                Best Practices:
              </Typography>
              <Box sx={{ bgcolor: '#e3f2fd', p: 2, borderRadius: 1 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  • Use high contrast colors for better visibility
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  • Keep text concise and impactful
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  • Ensure images work well on both PC and mobile
                </Typography>
                <Typography variant="body2">
                  • Test readability at different screen sizes
                </Typography>
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGuidelineDialogOpen(false)} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* 배너 수정 다이얼로그 */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Main Banner</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                    PC Image
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Button
                      variant="outlined"
                      startIcon={<CloudUploadIcon />}
                      onClick={() => handleUploadClick('pc', true)}
                      sx={{ minWidth: 120 }}
                    >
                      Upload PC Image
                    </Button>
                    {editPcImageFile && (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography variant="body2" color="success.main">
                          ✓ {editPcImageFile.name}
                        </Typography>
                        <Box
                          component="img"
                          src={URL.createObjectURL(editPcImageFile)}
                          alt="PC Image Preview"
                          sx={{
                            width: '100%',
                            maxWidth: 500,
                            height: 'auto',
                            maxHeight: 300,
                            objectFit: 'contain',
                            border: '1px solid #e0e0e0',
                            borderRadius: 1,
                            cursor: 'pointer',
                            '&:hover': {
                              opacity: 0.8,
                              borderColor: '#1976d2'
                            }
                          }}
                          onClick={() => window.open(URL.createObjectURL(editPcImageFile), '_blank')}
                          title="Click to view full image"
                        />
                      </Box>
                    )}
                  </Box>
                                      {!editPcImageFile && editingBanner?.pcImageUrl && (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography variant="body2" color="textSecondary">
                          Current Image:
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                          <Box
                            component="img"
                            src={editingBanner.pcImageUrl}
                            alt="PC Image Preview"
                            sx={{
                              width: '100%',
                              maxWidth: 500,
                              height: 'auto',
                              maxHeight: 300,
                              objectFit: 'contain',
                              border: '1px solid #e0e0e0',
                              borderRadius: 1,
                              cursor: 'pointer',
                              '&:hover': {
                                opacity: 0.8,
                                borderColor: '#1976d2'
                              }
                            }}
                            onClick={() => window.open(editingBanner.pcImageUrl, '_blank')}
                            title="Click to view full image"
                          />
                        </Box>
                      </Box>
                    )}
                  <input
                    id="edit-pc-image-upload"
                    type="file"
                    ref={editPcImageRef}
                    onChange={(e) => {
                      const file = (e.target as HTMLInputElement).files?.[0];
                      if (file) handleFileUpload(file, 'pc', true);
                    }}
                    style={{ display: 'none' }}
                    accept="image/jpeg,image/jpg,image/png,image/gif"
                  />
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                    Mobile Image
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Button
                      variant="outlined"
                      startIcon={<CloudUploadIcon />}
                      onClick={() => handleUploadClick('mobile', true)}
                      sx={{ minWidth: 120 }}
                    >
                      Upload Mobile Image
                    </Button>
                    {editMobileImageFile && (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography variant="body2" color="success.main">
                          ✓ {editMobileImageFile.name}
                        </Typography>
                        <Box
                          component="img"
                          src={URL.createObjectURL(editMobileImageFile)}
                          alt="Mobile Image Preview"
                          sx={{
                            width: '100%',
                            maxWidth: 500,
                            height: 'auto',
                            maxHeight: 300,
                            objectFit: 'contain',
                            border: '1px solid #e0e0e0',
                            borderRadius: 1,
                            cursor: 'pointer',
                            '&:hover': {
                              opacity: 0.8,
                              borderColor: '#1976d2'
                            }
                          }}
                          onClick={() => window.open(URL.createObjectURL(editMobileImageFile), '_blank')}
                          title="Click to view full image"
                        />
                      </Box>
                    )}
                  </Box>
                  {!editMobileImageFile && editingBanner?.mobileImageUrl && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Typography variant="body2" color="textSecondary">
                        Current Image:
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <Box
                          component="img"
                          src={editingBanner.mobileImageUrl}
                          alt="Mobile Image Preview"
                          sx={{
                            width: '100%',
                            maxWidth: 500,
                            height: 'auto',
                            maxHeight: 300,
                            objectFit: 'contain',
                            border: '1px solid #e0e0e0',
                            borderRadius: 1,
                            cursor: 'pointer',
                            '&:hover': {
                              opacity: 0.8,
                              borderColor: '#1976d2'
                            }
                          }}
                          onClick={() => window.open(editingBanner.mobileImageUrl, '_blank')}
                          title="Click to view full image"
                        />
                      </Box>
                    </Box>
                  )}
                  <input
                    id="edit-mobile-image-upload"
                    type="file"
                    ref={editMobileImageRef}
                    onChange={(e) => {
                      const file = (e.target as HTMLInputElement).files?.[0];
                      if (file) handleFileUpload(file, 'mobile', true);
                    }}
                    style={{ display: 'none' }}
                    accept="image/jpeg,image/jpg,image/png,image/gif"
                  />
                </Box>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Link URL"
                  value={editForm.linkUrl}
                  onChange={(e) => setEditForm({ ...editForm, linkUrl: e.target.value })}
                  margin="normal"
                  required
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Start Date (KST)"
                  type="datetime-local"
                  value={(() => {
                    // Unix timestamp는 이미 UTC 기준이므로, KST로 변환
                    const date = new Date(editForm.startDate * 1000);
                    // KST = UTC + 9시간
                    const kstDate = new Date(date.getTime() + (9 * 60 * 60 * 1000));
                    // datetime-local 형식: YYYY-MM-DDTHH:MM (초 제거)
                    const year = kstDate.getUTCFullYear();
                    const month = String(kstDate.getUTCMonth() + 1).padStart(2, '0');
                    const day = String(kstDate.getUTCDate()).padStart(2, '0');
                    const hours = String(kstDate.getUTCHours()).padStart(2, '0');
                    const minutes = String(kstDate.getUTCMinutes()).padStart(2, '0');
                    const result = `${year}-${month}-${day}T${hours}:${minutes}`;
                    
                    console.log('Start Date Display Debug:', {
                      originalTimestamp: editForm.startDate,
                      utcDate: date.toISOString(),
                      kstDate: kstDate.toISOString(),
                      displayValue: result
                    });
                    
                    return result;
                  })()}
                  onChange={(e) => {
                    // 사용자가 입력한 KST 시간을 UTC timestamp로 변환
                    const inputValue = e.target.value; // "2025-08-25T21:00:00"
                    
                    // 입력값을 직접 파싱하여 KST 시간 생성
                    const [datePart, timePart] = inputValue.split('T');
                    const [year, month, day] = datePart.split('-').map(Number);
                    const [hour, minute, second] = timePart.split(':').map(Number);
                    
                    // KST 시간을 UTC timestamp로 직접 변환
                    // KST = UTC + 9시간이므로, UTC = KST - 9시간
                    let utcHour = hour - 9;
                    let utcDay = day;
                    let utcMonth = month;
                    let utcYear = year;
                    
                    // 시간이 음수가 되면 이전 날로 조정
                    if (utcHour < 0) {
                      utcHour += 24;
                      utcDay -= 1;
                      // 날짜가 0이 되면 이전 달로 조정
                      if (utcDay <= 0) {
                        utcMonth -= 1;
                        if (utcMonth <= 0) {
                          utcMonth = 12;
                          utcYear -= 1;
                        }
                        // 해당 월의 마지막 날 계산
                        const lastDay = new Date(utcYear, utcMonth, 0).getDate();
                        utcDay = lastDay;
                      }
                    }
                    
                    const utcTimestamp = Date.UTC(utcYear, utcMonth - 1, utcDay, utcHour, minute, 0);
                    const timestamp = Math.floor(utcTimestamp / 1000);
                    

                    setEditForm({ ...editForm, startDate: timestamp });
                  }}
                  margin="normal"
                  required
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="End Date (KST)"
                  type="datetime-local"
                  value={(() => {
                    // Unix timestamp는 이미 UTC 기준이므로, KST로 변환
                    const date = new Date(editForm.endDate * 1000);
                    // KST = UTC + 9시간
                    const kstDate = new Date(date.getTime() + (9 * 60 * 60 * 1000));
                    // datetime-local 형식: YYYY-MM-DDTHH:MM (초 제거)
                    const year = kstDate.getUTCFullYear();
                    const month = String(kstDate.getUTCMonth() + 1).padStart(2, '0');
                    const day = String(kstDate.getUTCDate()).padStart(2, '0');
                    const hours = String(kstDate.getUTCHours()).padStart(2, '0');
                    const minutes = String(kstDate.getUTCMinutes()).padStart(2, '0');
                    const result = `${year}-${month}-${day}T${hours}:${minutes}`;
                    

                    
                    return result;
                  })()}
                  onChange={(e) => {
                    // 사용자가 입력한 KST 시간을 UTC timestamp로 변환
                    const inputValue = e.target.value; // "2025-09-24T21:00:59"
                    
                    // 입력값을 직접 파싱하여 KST 시간 생성
                    const [datePart, timePart] = inputValue.split('T');
                    const [year, month, day] = datePart.split('-').map(Number);
                    const [hour, minute, second] = timePart.split(':').map(Number);
                    
                    // KST 시간을 UTC timestamp로 직접 변환
                    // KST = UTC + 9시간이므로, UTC = KST - 9시간
                    let utcHour = hour - 9;
                    let utcDay = day;
                    let utcMonth = month;
                    let utcYear = year;
                    
                    // 시간이 음수가 되면 이전 날로 조정
                    if (utcHour < 0) {
                      utcHour += 24;
                      utcDay -= 1;
                      // 날짜가 0이 되면 이전 달로 조정
                      if (utcDay <= 0) {
                        utcMonth -= 1;
                        if (utcMonth <= 0) {
                          utcMonth = 12;
                          utcYear -= 1;
                        }
                        // 해당 월의 마지막 날 계산
                        const lastDay = new Date(utcYear, utcMonth, 0).getDate();
                        utcDay = lastDay;
                      }
                    }
                    
                    const utcTimestamp = Date.UTC(utcYear, utcMonth - 1, utcDay, utcHour, minute, 0);
                    const timestamp = Math.floor(utcTimestamp / 1000);
                    

                    setEditForm({ ...editForm, endDate: timestamp });
                  }}
                  margin="normal"
                  required
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Locale</InputLabel>
                  <Select
                    value={editForm.locale}
                    label="Locale"
                    onChange={(e) => setEditForm({ ...editForm, locale: e.target.value })}
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="en">English (en)</MenuItem>
                    <MenuItem value="ko">Korean (ko)</MenuItem>
                    <MenuItem value="ja">Japanese (ja)</MenuItem>
                    <MenuItem value="zh">Chinese (zh)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Weight"
                  type="number"
                  value={editForm.weight}
                  onChange={(e) => setEditForm({ ...editForm, weight: parseInt(e.target.value) })}
                  margin="normal"
                  required
                />
              </Grid>
              <Grid item xs={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={editForm.isActive}
                      onChange={(e) => setEditForm({ ...editForm, isActive: e.target.checked })}
                    />
                  }
                  label="Active Status"
                  sx={{ mt: 2 }}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleUpdateBanner} variant="contained">
            Update
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MainBannerTab;
